/**
 * Admin — Regulamento editor.
 *
 * Multi-language (PT/EN/ES) WYSIWYG editor for the public /regulamento page.
 * Uses the shared SimpleRichTextEditor (no placeholders shown — it's plain
 * page content, not a templated email).
 *
 * Backend:
 *   GET  /api/admin/content/regulamento  → { content_pt, content_en, content_es }
 *   PUT  /api/content/regulamento        → accepts partial { content_pt?, content_en?, content_es? }
 *
 * Save is idempotent and partial (only the currently-selected language body
 * is written on Save, so PT/EN/ES edits don't clobber each other if the
 * admin toggles tabs mid-edit).
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileText, Save, Eye, Loader2, Pencil, LayoutTemplate, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import SimpleRichTextEditor from "@/pages/admin/components/SimpleRichTextEditor";

const LANGS = [
  { code: "pt", label: "Português", flag: "PT" },
  { code: "en", label: "English",   flag: "EN" },
  { code: "es", label: "Español",   flag: "ES" },
];

// Tailwind classes historically injected by the legacy CustomPage wrappers
// that constrained the /regulamento content to ~768 px inside the A4 sheet.
// Any occurrence of these on a WRAPPING <div> is safe to strip.
const LEGACY_TAILWIND_CLASSES = /(^|\s)(mx-auto|w-full|max-w-(?:xs|sm|md|lg|xl|2xl|3xl|4xl|5xl|6xl|7xl|prose)|(?:sm|md|lg|xl|2xl):max-w-(?:xs|sm|md|lg|xl|2xl|3xl|4xl|5xl|6xl|7xl|prose)|px-(?:0|1|2|3|4|5|6|8|10|12|16)|(?:sm|md|lg|xl|2xl):px-(?:0|1|2|3|4|5|6|8|10|12|16))(?=\s|$)/g;

/**
 * Rewrites the given HTML by:
 *   1) Removing legacy Tailwind layout classes (max-w-*, mx-auto, px-*) from
 *      every element (they used to constrain the rules to a narrow strip).
 *   2) Unwrapping <div> elements that end up with NO attributes at all AND
 *      whose only purpose was to hold those classes — leaving inline
 *      content (<p>, <h*>, <ul>, etc.) as siblings inside the outer card.
 *
 * Returns `{ html, classesStripped, divsUnwrapped }` so the UI can tell the
 * admin exactly what changed.
 */
function cleanLegacyHtml(input) {
  if (!input || typeof input !== "string") {
    return { html: input || "", classesStripped: 0, divsUnwrapped: 0 };
  }
  // DOMParser needs a full document root for reliable parsing of fragments.
  const doc = new DOMParser().parseFromString(`<body><div id="__root__">${input}</div></body>`, "text/html");
  const root = doc.getElementById("__root__");
  let classesStripped = 0;
  let divsUnwrapped = 0;

  const walkAndClean = (el) => {
    Array.from(el.children).forEach(walkAndClean);
    if (el.hasAttribute && el.hasAttribute("class")) {
      const before = el.getAttribute("class");
      const after = before.replace(LEGACY_TAILWIND_CLASSES, " ").replace(/\s{2,}/g, " ").trim();
      if (after !== before) {
        const removedCount = (before.match(LEGACY_TAILWIND_CLASSES) || []).length;
        classesStripped += removedCount;
        if (after) el.setAttribute("class", after);
        else el.removeAttribute("class");
      }
    }
  };
  walkAndClean(root);

  // Second pass: unwrap layout-only <div>s (no attributes left). We do this
  // AFTER stripping classes so a wrapper that only had layout classes ends
  // up with `attributes.length === 0` and becomes eligible for unwrapping.
  const unwrapPass = (el) => {
    // Iterate over a snapshot because we'll mutate the tree.
    Array.from(el.children).forEach(unwrapPass);
    if (
      el.tagName === "DIV"
      && el !== root
      && el.attributes.length === 0
    ) {
      const parent = el.parentNode;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
      divsUnwrapped += 1;
    }
  };
  unwrapPass(root);

  return { html: root.innerHTML, classesStripped, divsUnwrapped };
}

export default function AdminRegulamento() {
  const [lang, setLang] = useState("pt");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bodies, setBodies] = useState({ content_pt: "", content_en: "", content_es: "" });
  const [dirty, setDirty] = useState({ pt: false, en: false, es: false });
  // "edit" shows the WYSIWYG editor; "preview" renders exactly like the public
  // /regulamento page (A4 width, same typography) so admins can proofread the
  // final layout without saving/deploying first.
  const [mode, setMode] = useState("edit");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/content/regulamento");
        setBodies({
          content_pt: data.content_pt || "",
          content_en: data.content_en || "",
          content_es: data.content_es || "",
        });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Erro ao carregar regulamento");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const currentValue = bodies[`content_${lang}`] || "";

  const handleChange = (html) => {
    setBodies((b) => ({ ...b, [`content_${lang}`]: html }));
    setDirty((d) => ({ ...d, [lang]: true }));
  };

  const save = async () => {
    setSaving(true);
    try {
      // Persist ALL bodies so the admin can Save once after editing multiple
      // languages. Partial-update backend contract still applies for callers.
      await api.put("/content/regulamento", {
        content_pt: bodies.content_pt || "",
        content_en: bodies.content_en || "",
        content_es: bodies.content_es || "",
      });
      toast.success("Regulamento atualizado");
      setDirty({ pt: false, en: false, es: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  const anyDirty = useMemo(() => dirty.pt || dirty.en || dirty.es, [dirty]);
  const previewUrl = lang === "pt" ? "/regulamento" : `/regulamento?lang=${lang}`;

  const handleCleanLegacy = () => {
    const key = `content_${lang}`;
    const current = bodies[key] || "";
    if (!current.trim()) {
      toast.info("Sem conteúdo neste idioma — nada para limpar.");
      return;
    }
    const { html, classesStripped, divsUnwrapped } = cleanLegacyHtml(current);
    if (classesStripped === 0 && divsUnwrapped === 0) {
      toast.success("Já está limpo — nenhum wrapper legacy encontrado.");
      return;
    }
    setBodies((prev) => ({ ...prev, [key]: html }));
    setDirty((prev) => ({ ...prev, [lang]: true }));
    const bits = [];
    if (classesStripped > 0) bits.push(`${classesStripped} classe${classesStripped === 1 ? "" : "s"} removida${classesStripped === 1 ? "" : "s"}`);
    if (divsUnwrapped > 0) bits.push(`${divsUnwrapped} <div> desembrulhado${divsUnwrapped === 1 ? "" : "s"}`);
    toast.success(`HTML limpo: ${bits.join(" · ")}. Não te esqueças de gravar.`);
  };

  // Same as handleCleanLegacy, applied to PT+EN+ES in a single click. Only
  // marks a language as dirty when it actually changes, so pristine idioms
  // stay untouched.
  const handleCleanLegacyAll = () => {
    let totalClasses = 0;
    let totalDivs = 0;
    const touched = [];
    const nextBodies = { ...bodies };
    const nextDirty = { ...dirty };
    for (const l of LANGS) {
      const key = `content_${l.code}`;
      const current = nextBodies[key] || "";
      if (!current.trim()) continue;
      const { html, classesStripped, divsUnwrapped } = cleanLegacyHtml(current);
      if (classesStripped === 0 && divsUnwrapped === 0) continue;
      nextBodies[key] = html;
      nextDirty[l.code] = true;
      totalClasses += classesStripped;
      totalDivs += divsUnwrapped;
      touched.push(l.flag);
    }
    if (totalClasses === 0 && totalDivs === 0) {
      toast.success("Todos os idiomas já estão limpos.");
      return;
    }
    setBodies(nextBodies);
    setDirty(nextDirty);
    const bits = [];
    if (totalClasses > 0) bits.push(`${totalClasses} classe${totalClasses === 1 ? "" : "s"}`);
    if (totalDivs > 0) bits.push(`${totalDivs} <div>`);
    toast.success(`Limpo em ${touched.join("+")}: ${bits.join(" · ")} removido${totalClasses + totalDivs === 1 ? "" : "s"}. Grava para persistir.`);
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="admin-regulamento">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-[Fredoka] text-3xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-7 h-7 text-sky-500" /> Regulamento
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Texto da página pública <code className="bg-slate-100 px-1.5 py-0.5 rounded">/regulamento</code>.
            Editor rich-text para cada idioma. As alterações ficam disponíveis imediatamente sem deploy.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Edit / Preview toggle — the preview panel replaces the editor
              with an A4-width render that mirrors the public /regulamento
              page (same typography, same content-injection). */}
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5" data-testid="regulamento-mode-toggle">
            <button
              onClick={() => setMode("edit")}
              data-testid="regulamento-mode-edit"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                mode === "edit"
                  ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              <Pencil className="w-3.5 h-3.5" /> Editar
            </button>
            <button
              onClick={() => setMode("preview")}
              data-testid="regulamento-mode-preview"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                mode === "preview"
                  ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              <LayoutTemplate className="w-3.5 h-3.5" /> Pré-visualizar
            </button>
          </div>
          <a
            href={previewUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50"
            data-testid="regulamento-preview-link"
          >
            <Eye className="w-4 h-4" /> Ver página pública
          </a>
          <Button
            onClick={handleCleanLegacy}
            variant="outline"
            className="rounded-lg border-amber-300 text-amber-800 hover:bg-amber-50"
            data-testid="regulamento-clean-legacy-btn"
            title="Remove wrappers Tailwind antigos (max-w-*, mx-auto, px-*) do HTML deste idioma"
          >
            <Sparkles className="w-4 h-4 mr-2" /> Limpar HTML legacy
          </Button>
          <Button
            onClick={handleCleanLegacyAll}
            variant="outline"
            className="rounded-lg border-amber-400 text-amber-900 hover:bg-amber-100"
            data-testid="regulamento-clean-legacy-all-btn"
            title="Corre a limpeza em PT+EN+ES numa só ação"
          >
            <Sparkles className="w-4 h-4 mr-2" /> Limpar todos
          </Button>
          <Button
            onClick={save}
            disabled={!anyDirty || saving || loading}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="regulamento-save"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar
          </Button>
        </div>
      </header>

      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm">
        <div className="flex items-center border-b border-slate-200 px-3 pt-3 gap-1">
          {LANGS.map((l) => (
            <button
              key={l.code}
              onClick={() => setLang(l.code)}
              data-testid={`regulamento-tab-${l.code}`}
              className={`px-4 py-2 text-sm font-semibold rounded-t-lg border-b-2 -mb-px transition-colors ${
                lang === l.code
                  ? "border-sky-500 text-sky-700 bg-sky-50"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
              }`}
            >
              <span className="mr-2 text-[10px] tracking-wider font-bold text-slate-400">{l.flag}</span>
              {l.label}
              {dirty[l.code] && (
                <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-amber-500" title="Não guardado" />
              )}
            </button>
          ))}
        </div>

        <div className="p-4">
          {loading ? (
            <div className="h-64 grid place-items-center text-slate-400 gap-2">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="text-sm">A carregar…</span>
            </div>
          ) : mode === "preview" ? (
            <RegulamentoPreview html={currentValue} />
          ) : (
            <SimpleRichTextEditor
              key={lang}
              value={currentValue}
              onChange={handleChange}
              placeholder="Escreve o regulamento neste idioma…"
              minHeight={360}
              placeholders={[]}
              testId={`regulamento-editor-${lang}`}
            />
          )}
        </div>
      </div>

      <p className="text-xs text-slate-500">
        Dica: se um idioma ficar vazio, a página pública mostra o texto em Português como fallback.
      </p>
    </div>
  );
}

/**
 * A4-width preview panel that mirrors `pages/Regulamento.jsx`. Rendering
 * logic (looksLikeHtml detection, prose classes, wrapper card, page bg) is
 * kept in sync with the public page so the admin sees the exact final look.
 */
function looksLikeHtml(s) {
  return /<\/?(p|h[1-6]|ul|ol|li|br|div|span|strong|em|a|b|i|u)\b/i.test(s || "");
}

function RegulamentoPreview({ html }) {
  const isHtml = looksLikeHtml(html);
  return (
    <div
      className="rounded-xl bg-slate-100 p-4 md:p-6 flex justify-center min-h-[420px]"
      data-testid="regulamento-preview-panel"
    >
      {/* Same responsive container as the public /regulamento page — matches
          the reference site https://eudactica.com/p/rsupertmatik. */}
      <div className="mx-auto px-4 md:px-8 w-full max-w-3xl xl:max-w-4xl 2xl:max-w-5xl font-[Nunito]">
        <h1 className="text-4xl font-[Fredoka] font-semibold text-slate-900 mb-6">
          Regulamento
        </h1>
        {html ? (
          isHtml ? (
            <div
              className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm leading-relaxed text-slate-700 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-2 [&_a]:text-sky-600 [&_a]:underline"
              data-testid="regulamento-preview-content"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          ) : (
            <div
              className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm whitespace-pre-wrap leading-relaxed text-slate-700"
              data-testid="regulamento-preview-content"
            >
              {html}
            </div>
          )
        ) : (
          <div className="bg-white rounded-3xl border border-dashed border-slate-300 p-12 text-center text-slate-400 shadow-sm" data-testid="regulamento-preview-empty">
            (sem conteúdo neste idioma — a página pública mostrará o texto em Português como fallback.)
          </div>
        )}
      </div>
    </div>
  );
}
